import { Button as BaseButton } from "@base-ui/react/button";
import clsx from "clsx";
import type { ReactNode } from "react";
import { Link, type LinkProps } from "react-router";
import { Icon, type IconName } from "../Icon/Icon";
import { Spinner } from "../Spinner/Spinner";
import styles from "./Button.module.css";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

interface ButtonLookProps {
  /** One primary button per view; everything else is secondary or ghost. */
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Leading icon. */
  icon?: IconName;
  /** Trailing icon (a chevron on a menu trigger, for example). */
  iconEnd?: IconName;
  fullWidth?: boolean;
  className?: string;
  children?: ReactNode;
}

export interface ButtonProps
  extends ButtonLookProps, Omit<BaseButton.Props, "className" | "style" | "children" | "render"> {
  /** Shows a spinner, sets aria-busy and blocks clicks while keeping focus on the button. */
  pending?: boolean;
  /** Label while pending, ending in an ellipsis ("Saving…"); defaults to the normal label. */
  pendingLabel?: ReactNode;
}

function ButtonContent({
  icon,
  iconEnd,
  pending,
  children,
}: Pick<ButtonProps, "icon" | "iconEnd" | "pending" | "children">) {
  return (
    <>
      {pending ? <Spinner size="sm" label={null} /> : icon ? <Icon name={icon} /> : null}
      {children !== undefined && children !== null ? <span className={styles.label}>{children}</span> : null}
      {iconEnd ? <Icon name={iconEnd} /> : null}
    </>
  );
}

export function Button({
  variant = "secondary",
  size = "md",
  icon,
  iconEnd,
  fullWidth,
  pending = false,
  pendingLabel,
  disabled,
  className,
  children,
  type = "button",
  focusableWhenDisabled = false,
  ...rest
}: ButtonProps) {
  return (
    <BaseButton
      {...rest}
      type={type}
      disabled={disabled || pending}
      focusableWhenDisabled={pending || focusableWhenDisabled}
      aria-busy={pending || undefined}
      className={clsx(styles.button, fullWidth && styles.fullWidth, className)}
      data-variant={variant}
      data-size={size}
      data-icon-only={children === undefined || children === null ? "" : undefined}
    >
      <ButtonContent icon={icon} iconEnd={iconEnd} pending={pending}>
        {pending && pendingLabel ? pendingLabel : children}
      </ButtonContent>
    </BaseButton>
  );
}

export interface ButtonLinkProps extends ButtonLookProps, Omit<LinkProps, "className" | "style" | "children"> {}

/** A link that looks like a button: navigation stays a real <a> (Cmd/Ctrl-click works). */
export function ButtonLink({
  variant = "secondary",
  size = "md",
  icon,
  iconEnd,
  fullWidth,
  className,
  children,
  ...linkProps
}: ButtonLinkProps) {
  return (
    <Link
      {...linkProps}
      className={clsx(styles.button, fullWidth && styles.fullWidth, className)}
      data-variant={variant}
      data-size={size}
    >
      <ButtonContent icon={icon} iconEnd={iconEnd}>
        {children}
      </ButtonContent>
    </Link>
  );
}
